import { Instant } from '@js-joda/core';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { all, call, delay, fork, getContext, put, race, select, take } from 'redux-saga/effects';
import { SUPERUSER_ACCESS_EXPIRATION_KEY } from 'src/api';
import { SYMBOL_CACHE_KEY } from 'src/lib/constants/storage-keys';
import { localforage } from 'src/lib/serialization/localForage';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { setSuperuserAccessEnabledUntil } from 'src/store/actions/admin';
import { completeBootstrap } from 'src/store/actions/app';
import { sessionValidated, setAccessToken, setMasqToken, setRefreshToken } from 'src/store/actions/auth';
import { setSymbolCache } from 'src/store/actions/ui';
import type { AuthState } from 'src/store/reducers/auth';
import type { SymbolCacheEntry } from 'src/store/reducers/ui';
import {
    retrieveStoredAccessToken,
    retrieveStoredMasqToken,
    retrieveStoredRefreshToken,
} from 'src/store/sagas/auth/helpers';
import type { SagaContext } from 'src/store/types';

const baseLogger = container.get('Logger').getSubLogger({ name: 'bootstrap-sagas' });

/** Max time to wait for setAccessTokenSaga to dispatch user details before giving up. */
const USER_DETAILS_TIMEOUT_MS = 5_000;
/** Max time to wait for the security package API response before giving up. */
const FETCH_SECURITY_PACKAGE_TIMEOUT_MS = 10_000;

export function* checkInitialAuthTokens() {
    const log = baseLogger.getSubLogger({ name: 'checkInitialAuthTokens' }, { component: 'auth' });

    const [refreshToken, accessToken, masqToken]: [
        Awaited<ReturnType<typeof retrieveStoredRefreshToken>>,
        Awaited<ReturnType<typeof retrieveStoredAccessToken>>,
        Awaited<ReturnType<typeof retrieveStoredMasqToken>>,
    ] = yield all([call(retrieveStoredRefreshToken), call(retrieveStoredAccessToken), call(retrieveStoredMasqToken)]);

    if (TRACE) {
        log.trace({
            message: 'Retrieved stored tokens',
            accessToken: accessToken
                ? { hasToken: typeof accessToken.token === 'string', tokenLength: accessToken.token?.length }
                : null,
            refreshToken: refreshToken
                ? { hasToken: typeof refreshToken.token === 'string', tokenLength: refreshToken.token?.length }
                : null,
            masqToken: masqToken
                ? { hasToken: typeof masqToken.token === 'string', tokenLength: masqToken.token?.length }
                : null,
        });
    }

    yield all({
        refreshToken: put(setRefreshToken(refreshToken, true)),
        accessToken: put(setAccessToken(accessToken, true)),
        masqToken: put(setMasqToken(masqToken, true)),
    });
}

function* initAdminState() {
    const superuserAccessExpiration: Instant | undefined = yield localforage.getItem(SUPERUSER_ACCESS_EXPIRATION_KEY);

    const isValidAccessTime = superuserAccessExpiration && superuserAccessExpiration.isAfter(Instant.now());

    if (isValidAccessTime) {
        yield put(setSuperuserAccessEnabledUntil(superuserAccessExpiration));
    }
}

function* initSymbolCache() {
    const symbolCache: SymbolCacheEntry[] | undefined = yield localforage.getItem(SYMBOL_CACHE_KEY);
    yield put(setSymbolCache(symbolCache || []));
}

/**
 * For managed OAuth services, proactively validate the session on app load.
 * This ensures we have a valid access token before making API calls,
 * rather than waiting for 401/403 responses.
 */
function* validateManagedSession() {
    const log = baseLogger.getSubLogger({ name: 'validateManagedSession' }, { component: 'auth' });
    try {
        const oauth2Service: SagaContext['oAuth2Client'] = yield getContext('oAuth2Client');

        log.info('Validating session');

        // Note: ensureValidSession() emits TOKEN_REFRESHED or SESSION_EXPIRED events,
        // which are handled by tokenSync saga to update Redux state
        const result: Awaited<ReturnType<typeof oauth2Service.ensureValidSession>> = yield call([
            oauth2Service,
            oauth2Service.ensureValidSession,
        ]);

        log.info('Result', {
            isValid: result.isValid,
            hasTokens: !!result.tokens,
        });
    } catch (error) {
        log.error('Error validating session', { error });
        // Don't throw - let the app continue and handle auth errors reactively
    }
}

/**
 * Pre-fetch the security package during bootstrap so that roles are already
 * in the React Query cache before the app renders. Without this, authenticated
 * users briefly see "upgrade subscription" placeholders while the security
 * package is fetched lazily by component-level hooks.
 */
export function* fetchInitialSecurityPackage() {
    const log = baseLogger.getSubLogger({ name: 'fetchInitialSecurityPackage' });

    // Check if user is authenticated
    const accessToken: AuthState['accessToken'] = yield select((state: RootState) => state.auth.accessToken);
    if (!accessToken?.token) {
        if (TRACE) {
            log.trace('No access token, skipping security package prefetch');
        }
        return;
    }

    // Wait for user details to be set (setAccessTokenSaga decodes JWT and dispatches setUserDetails).
    // Use a bounded wait so bootstrap cannot hang indefinitely if the action never arrives.
    let user: UserDetails | undefined = yield select((state: RootState) => state.auth.user);
    if (!user) {
        if (TRACE) {
            log.trace('Waiting for user details before fetching security package');
        }
        const { timedOut }: { timedOut?: true; userDetailsSet?: unknown } = yield race({
            userDetailsSet: take('user-details::set'),
            timedOut: delay(USER_DETAILS_TIMEOUT_MS),
        });
        if (timedOut) {
            log.warn('Timed out waiting for user details, skipping security package prefetch');
            return;
        }
        user = yield select((state: RootState) => state.auth.user);
    }
    if (!user?.id) {
        if (TRACE) {
            log.trace('No user ID available, skipping security package prefetch');
        }
        return;
    }

    try {
        const sagaContainer: NonNullable<SagaContext['container']> = yield getContext('container');
        const queryClient = sagaContainer.get('QueryClient');

        log.info('Prefetching security package for authenticated user');
        const { timedOut }: { timedOut?: true; result?: unknown } = yield race({
            result: call(() => queryClient.fetchQuery(getUserSecurityPackageQuery(user!.id))),
            timedOut: delay(FETCH_SECURITY_PACKAGE_TIMEOUT_MS),
        });
        if (timedOut) {
            log.warn('Security package prefetch timed out, will fetch lazily');
            return;
        }
        if (TRACE) {
            log.trace('Security package prefetched successfully');
        }
    } catch (error) {
        // Don't block bootstrap if security package fetch fails —
        // the app will fetch lazily via React Query hooks
        log.error('Failed to prefetch security package', { error });
    }
}

function* performBootstrapTasks() {
    const log = container.get('Logger').getSubLogger({ name: 'bootstrap-sagas' });

    if (TRACE) {
        log.trace({
            message: 'Performing application initialization',
            epochMs: Instant.now().toEpochMilli(),
            now: new Date(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    // Run these first in order to setup the rest of any API calls we may have

    yield call(initAdminState);
    yield call(checkInitialAuthTokens);

    // For managed OAuth, validate the session proactively before API calls
    yield call(validateManagedSession);

    // Signal that session validation is complete. Before this point, accessToken
    // in Redux may be a stale value loaded from storage that hasn't been verified
    // against the auth server. The native App state machine uses this to decide
    // whether to show the login flow or proceed to the dashboard.
    yield put(sessionValidated());

    // Pre-fetch security package so roles are available before first render,
    // preventing a flash of "upgrade subscription" placeholders
    yield call(fetchInitialSecurityPackage);

    // Makes detached forks; so basically, we are going to send a bunch
    // of tasks to go finish, then when they are done the whole saga will
    // finally complete. It will just hang out until the last of the tasks are
    // done, or error.
    yield fork(initSymbolCache);
}

export function* bootstrapAppSaga() {
    const isBootstrapped: boolean = yield select((state: RootState) => state.app.bootstrapped);

    if (!isBootstrapped) {
        // Call here
        yield call(performBootstrapTasks);

        yield put(completeBootstrap());
    }
}
